import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DeviceMobile, Desktop } from '@phosphor-icons/react';

interface SessionMetrics {
  totalSessions: number;
  uniqueVisitors: number;
  avgSessionDuration: number;
  bounceRate: number;
  sessionsChange: number;
  visitorsChange: number;
  durationChange: number;
  bounceRateChange: number;
  deviceBreakdown?: Array<{ name: string; value: number; color: string }>;
  trafficSources?: Array<{ name: string; value: number; color: string }>;
  timeline?: Array<{
    date: string;
    sessions: number;
    visitors: number;
  }>;
}

export default function SessionsChart({ data }: { data: SessionMetrics }) {
  if (!data) return null;

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}m ${secs}s`;
  };
  const formatChange = (value = 0) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  const totalDevices = (data.deviceBreakdown || []).reduce((sum, item) => sum + item.value, 0);
  const totalSources = (data.trafficSources || []).reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="space-y-5">
      {/* 4 Traffic KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-3.5 shadow-sm">
          <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-2">
            <span>Total Sessions</span>
            <span className="text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded text-[10px] font-bold">
              {formatChange(data.sessionsChange)}
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-[#202223] dark:text-white">
            {data.totalSessions.toLocaleString()}
          </div>
          <p className="text-xs text-gray-400 mt-1">All store visits</p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-3.5 shadow-sm">
          <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-2">
            <span>Unique Visitors</span>
            <span className="text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded text-[10px] font-bold">
              {formatChange(data.visitorsChange)}
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-[#202223] dark:text-white">
            {data.uniqueVisitors.toLocaleString()}
          </div>
          <p className="text-xs text-gray-400 mt-1">Distinct individual users</p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-3.5 shadow-sm">
          <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-2">
            <span>Avg. Session Duration</span>
            <span className="text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded text-[10px] font-bold">
              {formatChange(data.durationChange)}
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-[#202223] dark:text-white">
            {formatDuration(data.avgSessionDuration)}
          </div>
          <p className="text-xs text-gray-400 mt-1">Average time spent browsing</p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-3.5 shadow-sm">
          <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-2">
            <span>Bounce Rate</span>
            <span className="text-blue-600 bg-blue-50 dark:bg-blue-950/40 px-1.5 py-0.5 rounded text-[10px] font-bold">
              {formatChange(data.bounceRateChange)}
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-[#202223] dark:text-white">
            {data.bounceRate}%
          </div>
          <p className="text-xs text-gray-400 mt-1">Single-page exit rate</p>
        </div>
      </div>

      {/* Main Sessions Spline Area Chart */}
      <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-base font-bold text-black dark:text-white">
              Traffic Trajectory Over Time
            </h3>
            <p className="text-xs text-gray-500">
              Comparing total sessions and unique visitor volume
            </p>
          </div>
          <div className="flex items-center gap-4 text-xs font-semibold">
            <div className="flex items-center gap-2 text-[#2C6ECB]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#2C6ECB]" />
              <span>Total Sessions</span>
            </div>
            <div className="flex items-center gap-2 text-[#006838]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#006838]" />
              <span>Unique Visitors</span>
            </div>
          </div>
        </div>

        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.timeline} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
              <defs>
                <linearGradient id="sessionsAreaBlue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2C6ECB" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#2C6ECB" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="visitorsAreaGreen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#006838" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#006838" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E1E3E5" opacity={0.4} />
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 11 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 11 }} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="sessions"
                name="Sessions"
                stroke="#2C6ECB"
                strokeWidth={1.75}
                fill="url(#sessionsAreaBlue)"
              />
              <Area
                type="monotone"
                dataKey="visitors"
                name="Unique Visitors"
                stroke="#006838"
                strokeWidth={1.5}
                fill="url(#visitorsAreaGreen)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Device & Traffic Source Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm">
          <h3 className="text-sm font-bold text-black dark:text-white mb-4">
            Sessions by Device
          </h3>
          <div className="space-y-4">
            {(data.deviceBreakdown || []).map((item) => {
              const percentage = totalDevices > 0 ? (item.value / totalDevices) * 100 : 0;
              const Icon = item.name === 'Mobile' || item.name === 'Tablet' ? DeviceMobile : Desktop;
              return (
                <div key={item.name}>
                  <div className="flex justify-between text-xs font-semibold mb-1">
                    <span className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-gray-400" />
                      {item.name}
                    </span>
                    <span className="font-mono">{percentage.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 w-full bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${percentage}%`, backgroundColor: item.color }} />
                  </div>
                </div>
              );
            })}
            {totalDevices === 0 && <p className="text-xs text-gray-400">No device data for this period.</p>}
          </div>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm">
          <h3 className="text-sm font-bold text-black dark:text-white mb-4">
            Top Acquisition Channels
          </h3>
          <div className="space-y-3">
            {(data.trafficSources || []).map((item) => {
              const percentage = totalSources > 0 ? (item.value / totalSources) * 100 : 0;
              return (
              <div key={item.name}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium text-gray-700 dark:text-gray-300">{item.name}</span>
                  <span className="font-mono font-bold">{percentage.toFixed(1)}%</span>
                </div>
                <div className="h-1.5 w-full bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${percentage}%`, backgroundColor: item.color }} />
                </div>
              </div>
              );
            })}
            {totalSources === 0 && <p className="text-xs text-gray-400">No acquisition data for this period.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
